/* eslint-disable react/require-default-props */
import React, { useCallback } from 'react';
import { Table } from '@ui/molecules/Table';
import { Toggle, Button } from '@ui';
import { translate } from '@utils/i18n';
import { DeficiencyDetailsCell } from './DeficiencyDetailsCell';
import { DeficienciesFiltersBar } from './DeficienciesFiltersBar';
import { MassActions } from '../MassActions';
import { groupDeficiencies, GROUP_BY_OPTIONS } from '../groupBy';
import './DeficienciesTable.scss';
const NS = 'features.proposals.deficiencies_step';
const t = (key) => translate(key, { namespace: NS });
export function formatDeficiencySeverity(value, options = []) {
    if (!value)
        return '';
    const match = options.find((option) => option.value === value);
    if (match === null || match === void 0 ? void 0 : match.label)
        return match.label;
    return value
        .split('_')
        .filter(Boolean)
        .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
        .join(' ');
}
export function formatDateOpened(value) {
    if (!value)
        return '';
    const date = /^\d{4}-\d{2}-\d{2}$/.test(value)
        ? new Date(`${value}T00:00:00`)
        : new Date(value);
    if (Number.isNaN(date.getTime()))
        return '';
    return date.toLocaleDateString('en-US', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit'
    });
}
function IncludePhotosRenderer({ item, onToggleIncludePhotos }) {
    const allIncluded = item.includedCount === item.photoCount && item.photoCount > 0;
    return (React.createElement("div", { className: "deficiencies-table__include-photos-cell" },
        React.createElement(Toggle, { checked: allIncluded, onChange: (e) => {
                onToggleIncludePhotos === null || onToggleIncludePhotos === void 0 ? void 0 : onToggleIncludePhotos(item.id, e.target.checked);
            }, "aria-label": translate('include_photos_for', { namespace: NS, number: item.id }) }),
        React.createElement("span", { "aria-label": translate('photo_count', { namespace: NS, count: item.includedCount }) },
            item.includedCount,
            "/",
            item.photoCount)));
}
function RemoveRenderer({ item, onRemove }) {
    return (React.createElement(Button, { type: "button", variant: "highlighted", className: "deficiencies-table__remove-button", onClick: () => onRemove === null || onRemove === void 0 ? void 0 : onRemove(item.id), "aria-label": `${t('remove_deficiency')} ${item.id}` },
        React.createElement("i", { className: "fa-light fa-times", "aria-hidden": "true" })));
}
function PhotosRenderer({ item, onPhotoClick }) {
    if (!item.hasPhotos) {
        return (React.createElement("span", { className: "deficiencies-table__photo-count", "data-testid": `deficiency-photos-count-${item.id}` }, "0"));
    }
    return (React.createElement("button", { type: "button", className: "deficiencies-table__photo-count-button", "data-testid": `deficiency-photos-count-${item.id}`, onClick: () => onPhotoClick === null || onPhotoClick === void 0 ? void 0 : onPhotoClick(item.id), "aria-label": translate('view_photos_label', { namespace: NS, number: item.id }) }, item.photoCount));
}
function DateOpenedRenderer({ item }) {
    return React.createElement(React.Fragment, null, formatDateOpened(item.dateOpened));
}
function DetailsRendererCompact({ item }) {
    return React.createElement(DeficiencyDetailsCell, { item: item, compact: true });
}
function DetailsRendererFull({ item }) {
    return React.createElement(DeficiencyDetailsCell, { item: item, compact: false });
}
function buildSeverityRenderer(severityOptions) {
    function SeverityRenderer({ item }) {
        return React.createElement(React.Fragment, null, formatDeficiencySeverity(item.severity, severityOptions));
    }
    return SeverityRenderer;
}
// ---------------------------------------------------------------------------
// Column factories
// ---------------------------------------------------------------------------
// Percentage widths (IIM IssuesGrid pattern). Sum to 100% of the row after the
// checkbox column so table-layout:fixed does not equal-split unconstrained cols.
// Sum to 96% so the 4.4rem selector column is not squeezed to 0 by table-layout:fixed.
const COLUMN_WIDTHS = {
    id: '8%',
    question: '13%',
    answer: '6%',
    dateOpened: '12%',
    systemLabel: '10%',
    details: '10%',
    // Listing (fit-content) sizes to the nowrap details string; keep this column compact.
    detailsFlat: '10rem',
    severity: '8%',
    resolutionStatus: '14%',
    includePhotos: '11%',
    photos: '15%',
    actions: '4%'
};
function buildSharedColumns(severityOptions, grouped = false) {
    return [
        {
            dataKey: 'id',
            label: t('col_deficiency_number'),
            columnType: 'primary',
            width: COLUMN_WIDTHS.id
        },
        {
            dataKey: 'question',
            label: t('col_question'),
            width: COLUMN_WIDTHS.question
        },
        {
            dataKey: 'answer',
            label: t('col_answer'),
            width: COLUMN_WIDTHS.answer
        },
        {
            dataKey: 'dateOpened',
            label: t('col_date_opened'),
            width: COLUMN_WIDTHS.dateOpened,
            renderer: DateOpenedRenderer
        },
        {
            dataKey: 'systemLabel',
            label: t('col_system_asset'),
            width: COLUMN_WIDTHS.systemLabel
        },
        Object.assign(Object.assign({ dataKey: 'details', label: t('col_details'), width: grouped ? COLUMN_WIDTHS.details : COLUMN_WIDTHS.detailsFlat }, (grouped ? {} : { maxWidth: COLUMN_WIDTHS.detailsFlat })), { renderer: grouped ? DetailsRendererFull : DetailsRendererCompact }),
        {
            dataKey: 'severity',
            label: t('col_severity'),
            width: COLUMN_WIDTHS.severity,
            renderer: buildSeverityRenderer(severityOptions)
        },
        {
            dataKey: 'resolutionStatus',
            label: t('col_resolution_status'),
            width: COLUMN_WIDTHS.resolutionStatus
        }
    ];
}
function buildLinkedColumns(onToggleIncludePhotos, onRemove, severityOptions = [], grouped = false) {
    return [
        ...buildSharedColumns(severityOptions, grouped),
        {
            dataKey: 'includePhotos',
            label: t('include_photos'),
            width: COLUMN_WIDTHS.includePhotos,
            renderer: ({ item }) => (React.createElement(IncludePhotosRenderer, { item: item, onToggleIncludePhotos: onToggleIncludePhotos }))
        },
        {
            dataKey: 'actions',
            label: '',
            columnType: 'action',
            width: COLUMN_WIDTHS.actions,
            renderer: ({ item }) => React.createElement(RemoveRenderer, { item: item, onRemove: onRemove })
        }
    ];
}
function buildCandidateColumns(onPhotoClick, severityOptions = [], grouped = false) {
    return [
        ...buildSharedColumns(severityOptions, grouped),
        {
            dataKey: 'photos',
            label: t('col_photos'),
            columnType: 'action',
            width: COLUMN_WIDTHS.photos,
            renderer: ({ item }) => (React.createElement(PhotosRenderer, { item: item, onPhotoClick: onPhotoClick }))
        }
    ];
}
// ---------------------------------------------------------------------------
// Column visibility item factories
// Actions and photos columns are always visible and are NOT listed in
// Configure (they are never hidden). id is listed as required.
// ---------------------------------------------------------------------------
function buildLinkedVisibilityItems() {
    return [
        { key: 'id', label: t('col_deficiency_number'), required: true },
        { key: 'question', label: t('col_question') },
        { key: 'answer', label: t('col_answer') },
        { key: 'dateOpened', label: t('col_date_opened') },
        { key: 'systemLabel', label: t('col_system_asset') },
        { key: 'details', label: t('col_details') },
        { key: 'severity', label: t('col_severity') },
        { key: 'resolutionStatus', label: t('col_resolution_status') },
        { key: 'includePhotos', label: t('include_photos') }
    ];
}
function buildCandidateVisibilityItems() {
    return [
        { key: 'id', label: t('col_deficiency_number'), required: true },
        { key: 'question', label: t('col_question') },
        { key: 'answer', label: t('col_answer') },
        { key: 'dateOpened', label: t('col_date_opened') },
        { key: 'systemLabel', label: t('col_system_asset') },
        { key: 'details', label: t('col_details') },
        { key: 'severity', label: t('col_severity') },
        { key: 'resolutionStatus', label: t('col_resolution_status') }
    ];
}
// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------
export function DeficienciesTable({ mode, rows, isLoading = false, isError = false, selectedItems, onSelectionChange, onSelectAll, visibleColumnKeys, defaultColumnKeys, onApplyColumnVisibility, groupBy = null, onGroupByChange, filters, onFiltersChange, assetsForSelect = [], proposalId, onClearSelection, onToggleIncludePhotos, onRemove, onOpenDetail, onPhotoClick, severityOptions = [] }) {
    const allColumns = mode === 'linked'
        ? buildLinkedColumns(onToggleIncludePhotos, onRemove, severityOptions, !!groupBy)
        : buildCandidateColumns(onPhotoClick, severityOptions, !!groupBy);
    // Filter columns based on visibility; columns not in the visibility items
    // (i.e. always-on actions/photos) are never filtered out.
    const visibilityItems = mode === 'linked' ? buildLinkedVisibilityItems() : buildCandidateVisibilityItems();
    const configuredKeys = new Set(visibilityItems.map((item) => item.key));
    const columns = visibleColumnKeys !== undefined
        ? allColumns.filter((col) => !configuredKeys.has(col.dataKey) || visibleColumnKeys.includes(col.dataKey))
        : allColumns;
    const handleSelectionChange = useCallback((id, checked) => {
        onSelectionChange(Number(id), checked);
    }, [onSelectionChange]);
    const handleRowClick = useCallback(({ item }, event) => {
        const target = event.target;
        if (target.closest('.table-column--selector') ||
            target.closest('.qmb-ui-toggle-standalone') ||
            target.closest('[data-action-cell]') ||
            target.closest('button')) {
            return;
        }
        onOpenDetail === null || onOpenDetail === void 0 ? void 0 : onOpenDetail(item.id);
    }, [onOpenDetail]);
    // Partition rows by group-by; accordion handles expand/collapse natively.
    const tableData = groupBy ? groupDeficiencies(rows, groupBy) : rows;
    // filterBar — passed only when filter props are supplied (table view)
    const filterBarElement = filters !== undefined && onFiltersChange !== undefined ? (React.createElement(DeficienciesFiltersBar, { filters: filters, onFiltersChange: onFiltersChange, assetsForSelect: assetsForSelect, severityOptions: severityOptions })) : undefined;
    // toolbar — always present when table is rendered (Toolbar.Selection + optional actions)
    const toolbarConfig = {
        selectAllLabel: t('select_all_on_page'),
        groups: proposalId !== undefined && onClearSelection !== undefined
            ? [
                {
                    id: 'deficiency-mass-actions',
                    content: (React.createElement(MassActions, { mode: mode, proposalId: proposalId, selectedItems: selectedItems, onClearSelection: onClearSelection }))
                }
            ]
            : []
    };
    // columnVisibility — present when column-state props are supplied
    const columnVisibilityConfig = onApplyColumnVisibility !== undefined && defaultColumnKeys !== undefined
        ? Object.assign({ items: visibilityItems, visibleKeys: visibleColumnKeys !== null && visibleColumnKeys !== void 0 ? visibleColumnKeys : defaultColumnKeys, defaultKeys: defaultColumnKeys, onApply: onApplyColumnVisibility }, (onGroupByChange !== undefined
            ? {
                grouping: {
                    options: GROUP_BY_OPTIONS.map((opt) => ({
                        value: opt.value,
                        label: t(opt.labelKey)
                    })),
                    value: groupBy,
                    onChange: (value) => onGroupByChange(value)
                }
            }
            : {})) : undefined;
    return (React.createElement(Table, { data: tableData, columns: columns, isGrouped: !!groupBy, groupedPresentation: "accordion", variant: "listing", selectable: true, selectedItems: selectedItems, onSelectionChange: handleSelectionChange, onSelectAll: onSelectAll, getItemId: (item) => item.id, isLoading: isLoading, isError: isError, onRowClick: handleRowClick, hover: true, enableCustomScrollbar: true, filterBar: filterBarElement, toolbar: toolbarConfig, columnVisibility: columnVisibilityConfig }));
}
